/**
 * EquationRenderer — walks tokens once, splits into lines (`newline`)
 * and (within each line) into LHS/RHS at the first `align`. Picks the
 * right CSS layout per shape:
 *
 *   - single line, no align    → inline-flex row
 *   - multi-line, no align     → 1-column grid, justify-content: center
 *   - multi-line, with align   → 2-column grid (max-content max-content),
 *                                rhs left, lhs right — equals signs line
 *                                up across rows.
 *
 * The cursor is an integer in [0..tokens.length]. Clicking any gap
 * moves the caret; the renderer wires onClick onto each caret slot.
 */
import type { CSSProperties, ReactElement } from "react";
import { CAT_COLORS, type CategoryKey } from "./palette";
import { renderStructure, type SlotRenderCtx } from "./structureGlyphs";
import type { FocusPath, StructToken, Token } from "./tokens";

interface EquationRendererProps {
  tokens: Token[];
  cursor: number;
  onCaretMove: (i: number) => void;
  /** Display font size in px. Default 32. */
  displaySize?: number;
  /** Index of last-inserted token to "flash" briefly. */
  flashKey?: number | null;
  /** When true, tokens with a `cat` ink are tinted. */
  blockColors?: boolean;
  /**
   * Focus path of the slot the caret is currently in. Empty path =
   * editing the top-level token list (the inter-token caret shows).
   */
  focusPath?: FocusPath;
  /** Fired when a slot is clicked — the editor sets the focus path. */
  onSlotClick?: (path: FocusPath) => void;
}

interface Line {
  /** Indices into the original `tokens` array (excludes newline/align). */
  segments: Array<{ token: Token; idx: number }>;
  /** Index of the align token within `segments`, or -1. */
  alignAt: number;
}

function splitLines(tokens: Token[]): Line[] {
  const lines: Line[] = [];
  let cur: Line = { segments: [], alignAt: -1 };
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.kind === "newline") {
      lines.push(cur);
      cur = { segments: [], alignAt: -1 };
      continue;
    }
    if (t.kind === "align") {
      cur.alignAt = cur.segments.length;
      continue;
    }
    cur.segments.push({ token: t, idx: i });
  }
  lines.push(cur);
  return lines;
}

export function EquationRenderer({
  tokens, cursor, onCaretMove, displaySize = 32, flashKey = null, blockColors = true,
  focusPath = [], onSlotClick,
}: EquationRendererProps) {
  const lines = splitLines(tokens);
  const hasAlign = lines.some((l) => l.alignAt >= 0);
  const isMulti = lines.length > 1;

  const sharedStyle: CSSProperties = {
    fontFamily: "var(--lilia-font-serif, 'IBM Plex Serif', Georgia, serif)",
    fontSize: displaySize,
    lineHeight: 1.2,
    color: "var(--lilia-text)",
    letterSpacing: "0.005em",
    userSelect: "none",
  };

  // Shared options bundle threaded down through render helpers.
  const opts: RenderOpts = {
    cursor, onCaretMove, displaySize, flashKey, blockColors,
    focusPath, onSlotClick, totalLen: tokens.length,
  };

  // Fast path: single line, no align.
  if (!isMulti && !hasAlign) {
    return (
      <span style={{ ...sharedStyle, display: "inline-flex", alignItems: "center", flexWrap: "wrap" }}>
        {renderSegments(lines[0].segments, opts, true)}
      </span>
    );
  }

  // Multi-line. The grid varies by whether any line carries an align.
  const gridTemplate = hasAlign ? "max-content max-content" : "max-content";

  return (
    <div
      style={{
        ...sharedStyle,
        display: "grid",
        gridTemplateColumns: gridTemplate,
        justifyContent: "center",
        rowGap: Math.round(displaySize * 0.32),
        columnGap: hasAlign ? Math.round(displaySize * 0.18) : 0,
      }}
    >
      {lines.map((line, li) => {
        const isLast = li === lines.length - 1;
        if (hasAlign) {
          const lhs = line.alignAt >= 0 ? line.segments.slice(0, line.alignAt) : line.segments;
          const rhs = line.alignAt >= 0 ? line.segments.slice(line.alignAt) : [];
          return (
            <div key={li} style={{ display: "contents" }}>
              <span style={{ display: "inline-flex", alignItems: "center", justifySelf: "end" }}>
                {renderSegments(lhs, opts, isLast)}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", justifySelf: "start" }}>
                {renderSegments(rhs, opts, isLast)}
              </span>
            </div>
          );
        }
        return (
          <span key={li} style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
            {renderSegments(line.segments, opts, isLast)}
          </span>
        );
      })}
    </div>
  );
}

interface RenderOpts {
  cursor: number;
  onCaretMove: (i: number) => void;
  displaySize: number;
  flashKey: number | null;
  blockColors: boolean;
  focusPath: FocusPath;
  onSlotClick: ((path: FocusPath) => void) | undefined;
  totalLen: number;
}

function renderSegments(
  segments: Array<{ token: Token; idx: number }>,
  opts: RenderOpts,
  isTerminalLine: boolean,
) {
  const out: ReactElement[] = [];
  const leadIdx = segments.length === 0 ? opts.totalLen : segments[0].idx;
  out.push(
    <CaretSlot
      key={`c-lead-${leadIdx}`}
      idx={leadIdx}
      active={opts.cursor === leadIdx && opts.focusPath.length === 0}
      onCaretMove={opts.onCaretMove}
      displaySize={opts.displaySize}
    />,
  );
  segments.forEach((seg, j) => {
    out.push(
      <TokenView
        key={`t-${seg.idx}`}
        token={seg.token}
        idx={seg.idx}
        cursor={opts.cursor}
        displaySize={opts.displaySize}
        flash={opts.flashKey === seg.idx}
        blockColors={opts.blockColors}
        focusPath={opts.focusPath}
        onSlotClick={opts.onSlotClick}
      />,
    );
    const nextIdx = j + 1 < segments.length ? segments[j + 1].idx : opts.totalLen;
    const isTerminal = nextIdx === opts.totalLen;
    if (!isTerminal || isTerminalLine) {
      out.push(
        <CaretSlot
          key={`c-${seg.idx}-${nextIdx}`}
          idx={nextIdx}
          active={opts.cursor === nextIdx && opts.focusPath.length === 0}
          onCaretMove={opts.onCaretMove}
          displaySize={opts.displaySize}
        />,
      );
    }
  });
  return out;
}

function CaretSlot({ idx, active, onCaretMove, displaySize }: {
  idx: number; active: boolean; onCaretMove: (i: number) => void; displaySize: number;
}) {
  return (
    <span
      data-testid="math-caret-slot"
      data-caret-index={idx}
      onClick={() => onCaretMove(idx)}
      style={{
        display: "inline-block",
        width: 4,
        height: displaySize * 0.7,
        verticalAlign: "middle",
        cursor: "text",
        position: "relative",
      }}
    >
      {active && (
        <span
          style={{
            position: "absolute",
            top: "6%",
            bottom: "6%",
            left: 1,
            width: 2,
            background: "var(--lilia-primary)",
            animation: "mc-caret 1s steps(1) infinite",
            borderRadius: 1,
          }}
        />
      )}
    </span>
  );
}

function TokenView({
  token, idx, cursor, displaySize, flash, blockColors, focusPath, onSlotClick,
}: {
  token: Token; idx: number; cursor: number; displaySize: number; flash: boolean; blockColors: boolean;
  focusPath: FocusPath;
  onSlotClick: ((path: FocusPath) => void) | undefined;
}) {
  const cls = flash ? "mc-flash" : undefined;
  const cat = token.cat as CategoryKey | undefined;
  const ink = blockColors && cat ? CAT_COLORS[cat].ink : undefined;
  const baseStyle: CSSProperties = {
    display: "inline-block",
    padding: "0 1px",
    fontStyle: token.italic ? "italic" : "normal",
    margin: token.mx ? `0 ${token.mx * displaySize * 0.35}px` : 0,
    color: ink,
  };
  if (token.kind === "struct") {
    // The struct sits at top-level index `idx` (basePath []). Its slots
    // resolve their own absolute paths from there; the shared ctx carries
    // the absolute focus path so the right nested slot lights up.
    const ctx: SlotRenderCtx = { focusPath, cursor, displaySize, blockColors, onSlotClick };
    return (
      <span className={cls} style={{ display: "inline-block", padding: "0 2px", verticalAlign: "middle" }} data-idx={idx}>
        {renderStructure(token as StructToken, [], idx, ctx)}
      </span>
    );
  }
  if (token.kind === "bigop") {
    return (
      <span
        className={cls}
        style={{ ...baseStyle, fontSize: displaySize * (token.size ?? 1.4), lineHeight: 1, verticalAlign: "middle" }}
        data-idx={idx}
      >
        {token.g}
      </span>
    );
  }
  return (
    <span className={cls} style={baseStyle} data-idx={idx}>
      {token.g}
    </span>
  );
}
